import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Loader2, 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  ChevronLeft,
  ChevronRight,
  X,
  Repeat,
  Building2,
} from "lucide-react";
import { getPublicEvents } from "../services/api";

const API_URL = import.meta.env.VITE_API_URL;

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Convert Firestore timestamps to JS Date
const toJsDate = (val) => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val.toDate && typeof val.toDate === "function") return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === "string") return new Date(val);
  return new Date();
};

// Format date key (fallback when dateKey not available from backend)
const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse time to minutes
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.trim().split(/\s+/);
  if (parts.length < 2) return 0;
  const [hours, minutes] = parts[0].split(':').map(Number);
  const ampm = parts[1].toLowerCase();
  let totalHours = hours;
  if (ampm === 'pm' && hours < 12) totalHours += 12;
  if (ampm === 'am' && hours === 12) totalHours = 0;
  return totalHours * 60 + (minutes || 0);
};

// Format recurrence rule
const formatRecurrenceRule = (recurrence, event = null) => {
  if (!recurrence || recurrence.type === "never") return null;
  
  const { type, frequency, interval, byDay } = recurrence;
  
  const dayMap = {
    SU: 'Sun', MO: 'Mon', TU: 'Tue', WE: 'Wed',
    TH: 'Thu', FR: 'Fri', SA: 'Sat'
  };
  
  let baseText = "";
  
  if (type === "weekly" || frequency === "WEEKLY") {
    const days = byDay && byDay.length > 0 ? byDay.map(d => dayMap[d] || d).join(", ") : "";
    baseText = interval === 1 ? (days ? `Weekly (${days})` : "Weekly") : `Every ${interval} weeks`;
  } else if (type === "daily" || frequency === "DAILY") {
    baseText = interval === 1 ? "Daily" : `Every ${interval} days`;
  } else if (type === "monthly" || frequency === "MONTHLY") {
    baseText = interval === 1 ? "Monthly" : `Every ${interval} months`;
  } else {
    baseText = "Recurring";
  }
  
  return baseText;
};

// Get month data for calendar grid
const getMonthData = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  
  const days = [];
  
  // Previous month days
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
    days.push({
      date: new Date(year, month - 1, prevMonthLastDay - i),
      isCurrentMonth: false,
    });
  }
  
  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }
  
  // Next month days (complete the grid)
  const remainingDays = 42 - days.length; // 6 rows x 7 days
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }
  
  return days;
};

// Group events by date
const groupEventsByDate = (events) => {
  const grouped = {};
  
  events.forEach(event => {
    // Use dateKey from backend if available (timezone-correct)
    // Fall back to recurrenceInstanceId date, then event.date
    let dateKey;
    if (event.dateKey) {
      // Backend provides timezone-correct date key
      dateKey = event.dateKey;
    } else if (event.recurrenceInstanceId) {
      // recurrenceInstanceId format is "eventId_YYYY-MM-DD"
      const parts = event.recurrenceInstanceId.split('_');
      dateKey = parts.length > 1 ? parts[parts.length - 1] : formatDateKey(toJsDate(event.date));
    } else {
      dateKey = formatDateKey(toJsDate(event.date));
    }
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(event);
  });
  
  // Sort events within each day
  Object.keys(grouped).forEach(dateKey => {
    grouped[dateKey].sort((a, b) => parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time));
  });
  
  return grouped;
};

export default function EventMonthWidget() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const theme = searchParams.get("theme") || "light";
  const color = searchParams.get("color") || "bf9a2f";
  const widthParam = searchParams.get("width") || "max";
  const heightParam = searchParams.get("height") || "max";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masjidData, setMasjidData] = useState(null);
  const [masjidId, setMasjidId] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const isDark = theme === "dark";
  const accentColor = `#${color}`;
  const bgColor = isDark ? "#1a1a1a" : "#ffffff";
  const textColor = isDark ? "#ffffff" : "#0f172a";
  const mutedColor = isDark ? "#9ca3af" : "#64748b";
  const borderColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";
  const cardBg = isDark ? "#222" : "#fff";
  const sectionBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
  const outerBorder = isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.1)";

  // Container style with border and rounded corners
  const containerStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: bgColor,
    width: widthParam === "max" ? "100vw" : `${widthParam}px`,
    height: heightParam === "max" ? "100vh" : `${heightParam}px`,
    minHeight: "400px",
    borderRadius: widthParam === "max" && heightParam === "max" ? "0" : "12px",
    border: widthParam === "max" && heightParam === "max" ? "none" : outerBorder,
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative",
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthData = getMonthData(year, month);
  const eventsPerDay = groupEventsByDate(events);

  // Load masjid info
  useEffect(() => {
    if (!token) return;

    const loadMasjidInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/v1/widget-data/${token}`);
        if (!response.ok) throw new Error("Failed to load widget data");

        const data = await response.json();
        if (!data.ok) throw new Error(data.error || "Failed to load widget data");

        setMasjidData(data.masjid);
        setMasjidId(data.masjid.id || data.masjid.masjidId || data.masjid.key);
      } catch (err) {
        setError(err.message || "Failed to load masjid data");
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [token]);

  // Fetch events via API
  useEffect(() => {
    if (!masjidId) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        
        // Use the events API with masjid filter
        const result = await getPublicEvents({ 
          masjidId, 
          days: 90,  // 3 months
          limit: 200 
        });
        
        const eventsList = result.events || [];
        eventsList.sort((a, b) => toJsDate(a.date) - toJsDate(b.date));
        setEvents(eventsList);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err.message || "Failed to load events");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [masjidId]);

  const goToPreviousMonth = () => {
    const newDate = new Date(year, month - 1, 1);
    const today = new Date();
    if (newDate.getFullYear() < today.getFullYear() || 
        (newDate.getFullYear() === today.getFullYear() && newDate.getMonth() < today.getMonth())) {
      return;
    }
    setCurrentDate(newDate);
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    const newDate = new Date(year, month + 1, 1);
    const today = new Date();
    const twoMonthsFromNow = new Date(today.getFullYear(), today.getMonth() + 2, 1);
    if (newDate > twoMonthsFromNow) return;
    setCurrentDate(newDate);
    setSelectedDate(null);
  };

  const isToday = (date) => formatDateKey(date) === formatDateKey(new Date());
  
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const handleDayClick = (dayData) => {
    if (!dayData.isCurrentMonth) return;
    const dateKey = formatDateKey(dayData.date);
    const dayEvents = eventsPerDay[dateKey] || [];
    if (dayEvents.length > 0) {
      setSelectedDate(dateKey);
    }
  };

  const selectedDayEvents = selectedDate ? (eventsPerDay[selectedDate] || []) : [];

  // Event Detail Modal
  const EventDetailModal = ({ event, onClose }) => {
    if (!event) return null;

    const eventDate = toJsDate(event.date);
    const formattedDate = eventDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return (
      <div 
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div 
          style={{
            backgroundColor: cardBg,
            borderRadius: "16px",
            width: "90%",
            maxWidth: "400px",
            maxHeight: "80%",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${borderColor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: textColor, lineHeight: "1.3" }}>
                {event.title}
              </h3>
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: mutedColor, flexShrink: 0 }}>
                <X size={20} />
              </button>
            </div>
            {event.masjid && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                <Building2 size={14} color={accentColor} />
                <span style={{ fontSize: "13px", fontWeight: "600", color: accentColor }}>{event.masjid}</span>
              </div>
            )}
          </div>

          <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>WHEN</div>
              <div style={{ backgroundColor: sectionBg, borderRadius: "10px", padding: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <CalendarIcon size={18} color={accentColor} />
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: textColor }}>{formattedDate}</div>
                    {event.time && (
                      <div style={{ fontSize: "13px", color: mutedColor, marginTop: "2px" }}>
                        {event.time}{event.endTime && ` - ${event.endTime}`}
                      </div>
                    )}
                  </div>
                </div>
                {event.recurrence && event.recurrence.type !== "never" && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", paddingTop: "10px", borderTop: `1px solid ${borderColor}` }}>
                    <Repeat size={18} color={accentColor} />
                    <span style={{ fontSize: "13px", color: textColor }}>{formatRecurrenceRule(event.recurrence, event)}</span>
                  </div>
                )}
              </div>
            </div>

            {(event.location || event.sublocation) && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>WHERE</div>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", display: "block" }}
                >
                  <div style={{ backgroundColor: sectionBg, borderRadius: "10px", padding: "12px", cursor: "pointer", transition: "all 0.2s" }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = sectionBg}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                      <MapPin size={18} color={accentColor} style={{ flexShrink: 0, marginTop: "2px" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: accentColor }}>{event.location}</div>
                        {event.sublocation && <div style={{ fontSize: "13px", color: mutedColor, marginTop: "2px" }}>{event.sublocation}</div>}
                      </div>
                      <ChevronRight size={16} color={mutedColor} style={{ flexShrink: 0, marginTop: "2px" }} />
                    </div>
                  </div>
                </a>
              </div>
            )}

            {event.description && (
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: mutedColor, letterSpacing: "0.5px", marginBottom: "8px" }}>DESCRIPTION</div>
                <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.6", color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)", whiteSpace: "pre-wrap" }}>
                  {event.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!token) {
    return (
      <div style={{ ...containerStyle, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🕌</div>
          <p style={{ color: "#ef4444", fontSize: "18px", fontWeight: "600", margin: 0 }}>Missing Widget Token</p>
          <p style={{ color: mutedColor, fontSize: "14px", marginTop: "8px" }}>Please configure this widget from your masjid dashboard</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <Loader2 style={{ width: "48px", height: "48px", color: accentColor, animation: "spin 1s linear infinite" }} />
        <p style={{ marginTop: "16px", color: mutedColor, fontSize: "14px" }}>Loading events...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
          <span style={{ fontSize: "28px" }}>⚠️</span>
        </div>
        <p style={{ color: "#ef4444", fontSize: "16px", fontWeight: "600", margin: 0 }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ ...containerStyle, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ textAlign: "center", padding: "12px 16px", borderBottom: `1px solid ${borderColor}` }}>
        <h2 style={{ margin: "0 0 4px 0", fontSize: "18px", fontWeight: "700", color: accentColor, letterSpacing: "0.5px" }}>
          EVENT CALENDAR
        </h2>
        {masjidData?.name && (
          <p style={{ margin: "0", fontSize: "12px", color: mutedColor, fontWeight: "500" }}>{masjidData.name}</p>
        )}
      </div>

      {/* Month Navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${borderColor}` }}>
        <button 
          onClick={goToPreviousMonth}
          style={{ 
            background: "none", 
            border: `1px solid ${borderColor}`, 
            borderRadius: "8px",
            padding: "8px", 
            cursor: "pointer", 
            color: textColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ChevronLeft size={18} />
        </button>
        
        <span style={{ fontSize: "16px", fontWeight: "700", color: textColor }}>
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        
        <button 
          onClick={goToNextMonth}
          style={{ 
            background: "none", 
            border: `1px solid ${borderColor}`, 
            borderRadius: "8px",
            padding: "8px", 
            cursor: "pointer", 
            color: textColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Days of Week Header */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${borderColor}` }}>
        {DAYS_OF_WEEK.map(day => (
          <div key={day} style={{ padding: "8px", textAlign: "center" }}>
            <span style={{ fontSize: "11px", fontWeight: "600", color: mutedColor, textTransform: "uppercase" }}>{day}</span>
          </div>
        ))}
      </div>

      {/* Calendar Grid - Google Calendar Style */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridTemplateRows: "repeat(6, 1fr)", overflow: "hidden" }}>
        {monthData.map((dayData, index) => {
          const dateKey = formatDateKey(dayData.date);
          const dayEvents = eventsPerDay[dateKey] || [];
          const isTodayDate = isToday(dayData.date);
          const isSelected = selectedDate === dateKey;
          const isPast = isPastDate(dayData.date);
          
          return (
            <div
              key={index}
              onClick={() => handleDayClick(dayData)}
              style={{
                borderRight: `1px solid ${borderColor}`,
                borderBottom: `1px solid ${borderColor}`,
                padding: "4px",
                backgroundColor: isSelected ? `${accentColor}15` : (dayData.isCurrentMonth ? "transparent" : sectionBg),
                opacity: !dayData.isCurrentMonth ? 0.4 : (isPast ? 0.5 : 1),
                cursor: dayEvents.length > 0 && dayData.isCurrentMonth ? "pointer" : "default",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: "60px",
              }}
            >
              {/* Day Number */}
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "2px" }}>
                <span style={{
                  fontSize: "12px",
                  fontWeight: isTodayDate ? "700" : "500",
                  color: isTodayDate ? "#fff" : textColor,
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: isTodayDate ? accentColor : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  {dayData.date.getDate()}
                </span>
              </div>
              
              {/* Event Cards - Google Calendar Style */}
              <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: "1px" }}>
                {dayEvents.slice(0, 3).map((event, i) => (
                  <div
                    key={event.recurrenceInstanceId || event.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(event);
                    }}
                    style={{
                      fontSize: "10px",
                      fontWeight: "500",
                      color: "#fff",
                      backgroundColor: accentColor,
                      padding: "2px 4px",
                      borderRadius: "3px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      opacity: 0.9,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = "0.9"}
                  >
                    {event.time && <span style={{ marginRight: "4px" }}>{event.time.split(' ')[0]}</span>}
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div style={{ fontSize: "9px", fontWeight: "600", color: accentColor, paddingLeft: "4px" }}>
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Day Events Dialog */}
      {selectedDate && selectedDayEvents.length > 0 && (
        <div 
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            zIndex: 100,
          }}
          onClick={() => setSelectedDate(null)}
        >
          <div 
            style={{
              backgroundColor: cardBg,
              borderRadius: "16px",
              width: "90%",
              maxWidth: "420px",
              maxHeight: "80%",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between", 
              padding: "16px 20px", 
              borderBottom: `1px solid ${borderColor}`,
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: textColor }}>
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: mutedColor }}>
                  {selectedDayEvents.length} event{selectedDayEvents.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button 
                onClick={() => setSelectedDate(null)}
                style={{ 
                  background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)", 
                  border: "none", 
                  cursor: "pointer", 
                  padding: "8px", 
                  borderRadius: "8px",
                  color: textColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Events List */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
              {selectedDayEvents.map(event => (
                <div
                  key={event.recurrenceInstanceId || event.id}
                  onClick={() => setSelectedEvent(event)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px",
                    marginBottom: "8px",
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                    borderRadius: "10px",
                    borderLeft: `3px solid ${accentColor}`,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: textColor, marginBottom: "4px" }}>{event.title}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Clock size={12} color={mutedColor} />
                        <span style={{ fontSize: "12px", color: mutedColor }}>
                          {event.time}{event.endTime && ` - ${event.endTime}`}
                        </span>
                      </div>
                      {event.recurrence && event.recurrence.type !== "never" && (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Repeat size={11} color={accentColor} />
                          <span style={{ fontSize: "11px", color: accentColor, fontWeight: "500" }}>{formatRecurrenceRule(event.recurrence)}</span>
                        </div>
                      )}
                    </div>
                    {event.location && (
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}
                      >
                        <MapPin size={11} color={accentColor} />
                        <span style={{ fontSize: "11px", color: accentColor }}>{event.location}</span>
                      </a>
                    )}
                  </div>
                  <ChevronRight size={18} color={mutedColor} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: "10px", borderTop: `1px solid ${borderColor}`, textAlign: "center", flexShrink: 0 }}>
        <a href="https://muslimplus.org" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", color: mutedColor, textDecoration: "none" }}>
          <span>Powered by</span>
          <span style={{ fontWeight: "700", color: accentColor }}>Muslim+</span>
        </a>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}
